import { NextSteps } from '@onegov/react-component-library';
import { Button, Col, ComponentLoader, HorizontalRule, Row, SectionPage } from '@snsw/react-component-library';
import { IconDownload, IconNotificationSuccess } from '@snsw/react-component-library/build/Icons/system';
import { clearForm, clearPlantItemRenewalForm } from 'plantItemNew/redux/slices/formSlice';
import { cleanPdf, downloadSummaryPDF } from 'plantItemNew/redux/slices/pdfLinkSlice';
import { clearPlantItemLicenceTypes } from 'plantItemNew/redux/slices/plantItemLicenceTypesSlice';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ScrollToTop from 'shared/utils/scrollToTop';
import { validatePaymentCompleted } from 'shared/utils/Validation';
import styles from './PlantItemRenewalConfirmation.module.scss';

export const PlantItemRenewalConfirmation = (props) => {
  ScrollToTop();

  const passwordlessOidc = useSelector((state) => state.passwordlessOidc);
  const application = useSelector((state) => state.application);
  const pdfLink = useSelector((state) => state.pdfLink);

  const [emailSentTo, setEmailSentTo] = useState(sessionStorage['plant_emailAddress']);
  const [refNumber, setRefNumber] = useState(sessionStorage['plant_applicationNo']);
  const [applicationPaid, setApplicationPaid] = useState(sessionStorage['plant_renewal_paid']);

  const applicationNo = application ? application.applicationNo : null;
  const applicationId = application ? application.applicationId : null;
  const emailAddress = passwordlessOidc?.user?.profile?.email;

  const { pdfData, pdfLoading, pdfFailed, pdfSucceeded } = pdfLink;

  const dispatch = useDispatch();

  const jsonSteps = [
    {
      title: 'Confirmation email',
      description: 'You will get an email confirming you have submitted your application.',
    },
    {
      title: 'Payment receipt',
      description: 'If payment of your renewal fee is successful, we will email you a payment receipt.',
    },
    {
      title: 'Registration certificate(s)',
      description: 'Your registration certificate(s) will be emailed automatically if your application is successful.',
    },
  ];

  const fetchSummaryPDF = () => {
    dispatch(downloadSummaryPDF(sessionStorage['plant_applicationId']));
  };

  useEffect(() => {
    const script = document.createElement('script');

    script.src = process.env.REACT_APP_QUICK_FEED;
    script.async = true;

    document.body.appendChild(script);
    window.onload = function () {
      window.quickFeed.init({
        placeholder: 'feedbackContainer',
        label: 'How was your online experience today?',
        environment: process.env.REACT_APP_QUICK_FEED_ENVIRONMENT,
        sourceApplication: process.env.REACT_PLANT_ITEM_RENEWAL_SOURCE_APPLICATION,
        commentForThumbsUp: true,
      });
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    const checkApplicationPaid = async (applicationId) => {
      const resp = await validatePaymentCompleted(applicationId);
      const paid = resp.data.isCompleted && resp.data.applicationId == applicationId;

      sessionStorage['plant_renewal_paid'] = paid;
      setApplicationPaid(paid);
    };

    if (applicationNo != null) {
      sessionStorage['plant_applicationNo'] = applicationNo;
      setRefNumber(applicationNo);
    }

    if (applicationId) {
      sessionStorage['plant_applicationId'] = applicationId;
      checkApplicationPaid(applicationId).catch(console.error);
    }

    if (emailAddress) {
      sessionStorage['plant_emailAddress'] = emailAddress;
      setEmailSentTo(emailAddress);
    }
  }, [applicationNo, applicationId, emailAddress]);

  useEffect(() => {
    if (pdfData && !pdfLoading && !pdfFailed && pdfSucceeded) {
      let fileName = `${sessionStorage['plant_applicationNo']}_Application.pdf`;
      const blob = new Blob([pdfData], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(cleanPdf());
    }
  }, [pdfData, pdfLoading, pdfFailed, pdfSucceeded]);

  useEffect(() => {
    dispatch(clearForm());
    dispatch(clearPlantItemLicenceTypes());
    dispatch(clearPlantItemRenewalForm());
  }, [dispatch]);

  return (
    <>
      {applicationPaid && (
        <div className={styles.paymentSuccess}>
          <Row>
            <Col span={9}>
              <SectionPage>
                <div className={styles.submittedTitle}>
                  <IconNotificationSuccess />
                  <span>You have submitted your renewal application.</span>
                </div>
                <div className={styles.confirmationText}>
                  <p>
                    We will send a confirmation email to
                    {' ' + emailSentTo}.
                  </p>
                  <p>
                    <b>Application number : </b>
                    {refNumber}
                  </p>
                  <p>You can download a copy of your renewal application now.</p>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => {
                    fetchSummaryPDF();
                  }}
                >
                  <IconDownload color="secondaryBlue" /> Download
                </Button>
              </SectionPage>
              <div className={styles.nextSteps}>
                <NextSteps items={jsonSteps} title="Next steps" />
              </div>
              <p>
                If you need to discuss your application contact{' '}
                <a href="https://www.safework.nsw.gov.au/">SafeWork NSW</a> on 13 10 50.
              </p>
            </Col>
          </Row>
        </div>
      )}

      <Row>
        <Col span={9}>
          <HorizontalRule />
          <div id="feedbackContainer" />
        </Col>
      </Row>

      {pdfLoading || !applicationPaid ? (
        <Row>
          <Col span={12}>
            <ComponentLoader fullPage />
          </Col>
        </Row>
      ) : null}
    </>
  );
};
